import axios from 'axios';

// Runtime config type
declare global {
  interface Window {
    __CLIPOID_CONFIG__?: {
      API_BASE_URL: string;
      MEDIA_BASE_URL: string;
      EDITOR_URL: string;
      FRONTEND_URL: string;
      ADMIN_URL: string;
      WS_URL: string;
      SOCKET_URL: string;
    };
  }
}

// This is a placeholder for logout logic that might be needed by the API instance
let logoutUserCallback: (isUserInitiated?: boolean) => void = () => {};

export const setLogoutUserCallback = (callback: (isUserInitiated?: boolean) => void) => {
  logoutUserCallback = callback;
};

// This is a placeholder for token refresh logic
let refreshTokenCallback: () => Promise<string | null> = async () => null;

export const setRefreshTokenCallback = (callback: () => Promise<string | null>) => {
  refreshTokenCallback = callback;
};


// Use runtime config if available, fallback to env vars or defaults
const getRuntimeConfig = () => {
  if (typeof window !== 'undefined' && window.__CLIPOID_CONFIG__) {
    return window.__CLIPOID_CONFIG__;
  }
  return null;
};

const runtimeConfig = getRuntimeConfig();

const API_BASE_URL = runtimeConfig?.API_BASE_URL || import.meta.env['VITE_API_BASE_URL'] || 'https://api.clipoid.com/api';
export const MEDIA_BASE_URL = runtimeConfig?.MEDIA_BASE_URL || import.meta.env['VITE_MEDIA_BASE_URL'] || 'https://api.clipoid.com';

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Export helper function for components that need to ensure CSRF token
export { ensureCsrfToken };

// Function to get CSRF token from cookies with retry logic
function getCsrfToken(): string | null {
  const csrfCookie = document.cookie.split('; ').find(row => row.startsWith('XSRF-TOKEN='));
  const token = csrfCookie ? csrfCookie.split('=')[1] : null;
  if (token) {
    // URL decode the token if it's encoded
    try {
      return decodeURIComponent(token);
    } catch (e) {
      console.warn('[API] Failed to decode CSRF token:', e);
      return token;
    }
  }
  return null;
}

// Function to ensure CSRF token exists
async function ensureCsrfToken(): Promise<string | null> {
  let token = getCsrfToken();
  
  // If no token, try to get one from the server
  if (!token) {
    console.log('[API] No CSRF token found, fetching from server...');
    try {
      await axios.get(`${API_BASE_URL}/auth/csrf-token`, { withCredentials: true });
      // After the request, the server should have set the CSRF cookie
      token = getCsrfToken();
      if (token) {
        console.log('[API] CSRF token successfully obtained from server');
      }
    } catch (error) {
      console.error('[API] Failed to fetch CSRF token:', error);
    }
  }
  
  return token;
}

// The getAuthTokens function has been removed as it was attempting to read
// HttpOnly cookies, which is not possible and was causing misleading logs.

// Add a request interceptor to attach CSRF token and log auth tokens
api.interceptors.request.use(async config => {
  const methodsToProtect = ['post', 'put', 'delete', 'patch'];
  const method = config.method?.toLowerCase();

  // The logic to get and log HttpOnly cookies from JS was flawed and has been removed
  // as HttpOnly cookies are not accessible via document.cookie.
  // The browser handles sending them automatically.

  if (method && methodsToProtect.includes(method)) {
    // Use ensureCsrfToken to guarantee we have a token
    const csrfToken = await ensureCsrfToken();
    console.log('[API] CSRF Token for', method.toUpperCase(), config.url, ':', csrfToken ? `present (${csrfToken.substring(0, 10)}...)` : 'missing');
    if (csrfToken) {
      config.headers['x-csrf-token'] = csrfToken;
    } else {
      console.error('[API] No CSRF token available for', method.toUpperCase(), config.url);
    }
  }
  return config;
}, error => {
  return Promise.reject(error);
});


// --- Advanced Interceptor with Race Condition Handling ---

let isRefreshing = false;
let failedQueue: { resolve: (value: unknown) => void; reject: (reason?: any) => void; }[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // console.log(`[API Interceptor] Received error: ${error.response?.status} for URL: ${originalRequest.url}`);

    const errorCode = error.response?.data?.errorCode || error.response?.data?.code;

    if (error.response.status === 401 && errorCode === 'TOKEN_INVALID') {
      // If the token is explicitly invalid (e.g., revoked), don't try to refresh.
      // Log out immediately.
      // console.log('[API Interceptor] Received 401 with TOKEN_INVALID errorCode. Logging out.');
      logoutUserCallback(false); // System initiated logout
      return Promise.reject(error);
    }
    
    // Handle TOKEN_EXPIRED - this should trigger a refresh attempt
    if (error.response.status === 401 && errorCode === 'TOKEN_EXPIRED') {
      console.log('[API Interceptor] Token expired, will attempt refresh...');
      // Continue to refresh logic below
    }

    // Skip token refresh for OAuth initiation endpoints - they don't require authentication
    const oauthInitiationEndpoints = ['/auth/google', '/auth/twitch', '/auth/kick'];
    const isOAuthInitiation = oauthInitiationEndpoints.some(endpoint => originalRequest.url?.includes(endpoint) && !originalRequest.url?.includes('callback'));
    
    if (error.response.status === 401 && isOAuthInitiation) {
      // console.log('[API Interceptor] 401 on OAuth initiation endpoint - not attempting refresh as these should not require auth');
      return Promise.reject(error);
    }

    if (error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // console.log('[API Interceptor] Token refresh in progress. Queuing request.');
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            // HttpOnly cookies are used - no need to set Authorization header
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;
      // console.log('[API Interceptor] 401 received. Starting token refresh...');

      return new Promise((resolve, reject) => {
        refreshTokenCallback()
          .then(newAccessToken => {
            if (newAccessToken) {
              // console.log('[API Interceptor] Token refresh successful.');
              // HttpOnly cookies are used - no need to set Authorization header
              // The browser will automatically include the new cookies in subsequent requests
              processQueue(null, newAccessToken);
              resolve(api(originalRequest));
            } else {
              const refreshError = new Error("Token refresh failed but did not throw.");
              // console.log('[API Interceptor] Token refresh failed, logging out and rejecting queue.');
              processQueue(refreshError, null);
              logoutUserCallback(false); // System initiated logout
              reject(refreshError);
            }
          })
          .catch(refreshError => {
            console.error('[API Interceptor] Error during token refresh attempt. Rejecting queue.', refreshError);
            processQueue(refreshError, null);
            logoutUserCallback(false); // System initiated logout
            reject(refreshError);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }
    
    if (error.response.status === 401 && originalRequest._retry) {
        // console.log('[API Interceptor] 401 received on retry, or refresh failed. Logging out.');
        logoutUserCallback(false); // System initiated logout
    }

    // Handle CSRF and session readiness errors with retry logic
    if ((error.response.status === 403 && error.response.data?.error === 'Invalid CSRF token.') || 
        (error.response.status === 503 && error.response.data?.code === 'SESSION_NOT_READY')) {
      
      if (!originalRequest._csrfRetry) {
        originalRequest._csrfRetry = true;
        
        const retryDelay = error.response.status === 503 
          ? (error.response.data?.retryAfter || 1000) 
          : 500; // 500ms for CSRF token issues
        
        // console.log(`[API Interceptor] ${error.response.status === 503 ? 'Session not ready' : 'CSRF token invalid'}, retrying in ${retryDelay}ms...`);
        
        return new Promise((resolve) => {
          setTimeout(resolve, retryDelay);
        }).then(async () => {
          // Reinitialize CSRF token for 403 errors
          if (error.response.status === 403) {
            try {
              await api.get('/auth/csrf-token');
              // console.log('[API Interceptor] CSRF token refreshed, retrying original request');
            } catch (csrfError) {
              console.warn('[API Interceptor] Failed to refresh CSRF token:', csrfError);
            }
          }
          return api(originalRequest);
        });
      } else {
        // console.log(`[API Interceptor] ${error.response.status === 503 ? 'Session' : 'CSRF'} retry already attempted, failing request`);
      }
    }

    // console.log('[API Interceptor] Rejecting promise for non-401 or retried error:', error.response?.status, error.message);
    return Promise.reject(error);
  }
);


// Standalone functions that were previously in 'common' can be defined here
// or in their own utility files within the frontend package.

export const initApi = async () => {
  try {
    // Make a call to an endpoint that is protected by csurf to get the initial token
    // Using a dedicated endpoint is cleaner. Let's assume one exists at /api/auth/csrf-token
    await api.get('/auth/csrf-token'); // Hitting any csurf-protected GET endpoint works
    // console.log("CSRF token initialized successfully.");
  } catch (error) {
    console.error("Failed to initialize CSRF token:", error);
  }
  // console.log("Local frontend API initialized.");
};

export const retryJob = async (jobId: string) => {
    return api.post(`/jobs/${jobId}/retry`);
};

export const redetectHighlightsJob = async (jobId: string, prompt: string, additionalClipCount?: number, deletePreviousClips?: boolean) => {
    return api.post(`/jobs/${jobId}/redetect-highlights`, {
        prompt,
        additionalClipCount,
        deletePreviousClips
    });
};

export const refreshJobThumbnail = async (jobId: string) => {
    return api.put(`/jobs/${jobId}/refresh-thumbnail`);
};

export const exchangeKickCode = async (code: string, codeVerifier: string, state: string) => {
    return api.post('/auth/kick/exchange-code', { code, codeVerifier, state });
};

export const deleteClips = async (clipIds: string[]) => {
    return api.delete('/clips', { data: { clipIds } });
};

export { API_BASE_URL };